import { useCallback, useEffect, useRef, useState } from "react";
import { Download, FileText, Pencil, PlayCircle, Trash2, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Card, SectionTitle, TornaHome } from "@/components/Kpi";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CHUNK = 4 * 1024 * 1024;
const SEZIONI = {
  procedure: { titolo: "PROCEDURE", sotto: "Archivio delle procedure operative in PDF", accept: ".pdf", nota: "Solo file PDF" },
  inxpire: { titolo: "INXPIRE", sotto: "Documenti PDF e video tutorial", accept: ".pdf,.mp4,.mov,.webm,.m4v", nota: "PDF o video (mp4, mov, webm)" },
};
const mb = (n) => (n ? `${(n / 1024 / 1024).toFixed(n > 10 * 1024 * 1024 ? 0 : 1)} MB` : "");

/** Form di caricamento a blocchi con barra di avanzamento. */
function Caricamento({ sezione, onDone }) {
  const cfg = SEZIONI[sezione];
  const [file, setFile] = useState(null);
  const [titolo, setTitolo] = useState("");
  const [descrizione, setDescrizione] = useState("");
  const [prog, setProg] = useState(null);
  const input = useRef(null);
  const invia = async () => {
    if (!file) return;
    const uploadId = crypto.randomUUID();
    const totale = Math.ceil(file.size / CHUNK);
    try {
      for (let i = 0; i < totale; i++) {
        const form = new FormData();
        form.append("upload_id", uploadId);
        form.append("indice", String(i));
        form.append("file", file.slice(i * CHUNK, (i + 1) * CHUNK), `parte-${i}`);
        await api.post(`/documenti/${sezione}/chunk`, form, { headers: { "Content-Type": "multipart/form-data" } });
        setProg(Math.round(((i + 1) / totale) * 90));
      }
      const fine = new FormData();
      fine.append("upload_id", uploadId);
      fine.append("nome", file.name);
      fine.append("titolo", titolo);
      fine.append("descrizione", descrizione);
      await api.post(`/documenti/${sezione}/completa`, fine, { headers: { "Content-Type": "multipart/form-data" } });
      setProg(100);
      toast.success(`"${titolo || file.name}" caricato`);
      setFile(null); setTitolo(""); setDescrizione(""); if (input.current) input.current.value = "";
      onDone();
    } catch (e) { toast.error(apiError(e)); } finally { setTimeout(() => setProg(null), 800); }
  };
  return (
    <Card className="p-5" data-testid="documenti-upload">
      <div className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500 mb-3">Carica un nuovo file · {cfg.nota}</div>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_2fr_auto] gap-3 items-end">
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-slate-400">File</span>
          <input ref={input} type="file" accept={cfg.accept} data-testid="documenti-file-input"
            onChange={(e) => { const f = e.target.files?.[0] || null; setFile(f); if (f && !titolo) setTitolo(f.name.replace(/\.[^.]+$/, "")); }}
            className="block w-full text-sm text-slate-600 file:mr-3 file:rounded-md file:border-0 file:bg-slate-900 file:text-white file:px-3 file:py-2 file:text-sm" />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-slate-400">Titolo</span>
          <input value={titolo} onChange={(e) => setTitolo(e.target.value)} data-testid="documenti-titolo"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" placeholder="es. Procedura anticipo provvigioni" />
        </label>
        <label className="block">
          <span className="text-[11px] uppercase tracking-wider text-slate-400">Descrizione (facoltativa)</span>
          <input value={descrizione} onChange={(e) => setDescrizione(e.target.value)} data-testid="documenti-descrizione"
            className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm" />
        </label>
        <button type="button" onClick={invia} disabled={!file || prog !== null} data-testid="documenti-carica-btn"
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#0018A8] text-white text-sm hover:bg-[#001487] disabled:opacity-50">
          <Upload size={16} /> {prog !== null ? `${prog}%` : "Carica"}
        </button>
      </div>
      {prog !== null && (
        <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden" data-testid="documenti-progress">
          <div className="h-full bg-[#0018A8] transition-[width] duration-300" style={{ width: `${prog}%` }} />
        </div>
      )}
      {file && <div className="mt-2 text-xs text-slate-500">{file.name} · {mb(file.size)}</div>}
    </Card>
  );
}

/** Riga di un documento con anteprima inline (PDF in iframe, video con player). */
function Documento({ d, puo, onChange }) {
  const [aperto, setAperto] = useState(false);
  const [edit, setEdit] = useState(false);
  const [titolo, setTitolo] = useState(d.titolo);
  const [descrizione, setDescrizione] = useState(d.descrizione);
  const url = `/api/documenti/file/${d.id}`;
  const salva = async () => {
    try { await api.patch(`/documenti/file/${d.id}`, { titolo, descrizione }); setEdit(false); onChange(); toast.success("Aggiornato"); }
    catch (e) { toast.error(apiError(e)); }
  };
  const elimina = async () => {
    if (!window.confirm(`Eliminare "${d.titolo}"?`)) return;
    try { await api.delete(`/documenti/file/${d.id}`); onChange(); toast.success("Eliminato"); } catch (e) { toast.error(apiError(e)); }
  };
  const Icona = d.tipo === "video" ? PlayCircle : FileText;
  return (
    <Card className="p-0 overflow-hidden" data-testid={`documento-${d.id}`}>
      <div className="flex flex-wrap items-center gap-3 px-5 py-4">
        <button type="button" onClick={() => setAperto((v) => !v)} data-testid={`documento-apri-${d.id}`}
          className="flex items-center gap-3 flex-1 min-w-0 text-left">
          <span className={`grid place-items-center h-10 w-10 rounded-lg shrink-0 ${d.tipo === "video" ? "bg-violet-50 text-violet-600" : "bg-red-50 text-red-600"}`}>
            <Icona size={20} />
          </span>
          <span className="min-w-0">
            {edit ? (
              <input value={titolo} onChange={(e) => setTitolo(e.target.value)} onClick={(e) => e.stopPropagation()}
                className="w-full rounded-md border border-slate-300 px-2 py-1 text-sm" data-testid={`documento-titolo-${d.id}`} />
            ) : <span className="block font-semibold text-slate-900 truncate">{d.titolo}</span>}
            {edit ? (
              <input value={descrizione} onChange={(e) => setDescrizione(e.target.value)} onClick={(e) => e.stopPropagation()}
                className="mt-1 w-full rounded-md border border-slate-300 px-2 py-1 text-xs" placeholder="descrizione" />
            ) : d.descrizione ? <span className="block text-sm text-slate-600 truncate">{d.descrizione}</span> : null}
            <span className="block text-[11px] text-slate-400 mt-0.5">{d.nome} · {mb(d.size)}</span>
          </span>
        </button>
        <div className="flex items-center gap-2 text-sm">
          {edit ? (
            <>
              <button type="button" onClick={salva} className="px-3 py-1.5 rounded-md bg-slate-900 text-white text-xs" data-testid={`documento-salva-${d.id}`}>Salva</button>
              <button type="button" onClick={() => setEdit(false)} className="text-slate-500 hover:text-slate-800"><X size={16} /></button>
            </>
          ) : (
            <>
              <a href={`${url}?download=true`} data-testid={`documento-scarica-${d.id}`} title="Scarica"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-100 text-xs">
                <Download size={14} /> Scarica
              </a>
              {puo && (
                <>
                  <button type="button" onClick={() => setEdit(true)} title="Rinomina" className="text-slate-400 hover:text-[#0018A8]" data-testid={`documento-modifica-${d.id}`}><Pencil size={16} /></button>
                  <button type="button" onClick={elimina} title="Elimina" className="text-slate-400 hover:text-[#C8102E]" data-testid={`documento-elimina-${d.id}`}><Trash2 size={16} /></button>
                </>
              )}
            </>
          )}
        </div>
      </div>
      {aperto && (
        <div className="border-t border-slate-100 bg-slate-50 p-3" data-testid={`documento-anteprima-${d.id}`}>
          {d.tipo === "video"
            ? <video src={url} controls preload="metadata" className="w-full max-h-[70vh] rounded-lg bg-black" />
            : <iframe src={url} title={d.titolo} className="w-full h-[75vh] rounded-lg bg-white" />}
        </div>
      )}
    </Card>
  );
}

export default function Documenti({ sezione }) {
  const cfg = SEZIONI[sezione];
  const { user } = useAuth();
  const [dati, setDati] = useState({ rows: [], puo_caricare: false });
  const [cerca, setCerca] = useState("");
  const carica = useCallback(() => {
    api.get(`/documenti/${sezione}`).then((r) => setDati(r.data)).catch((e) => toast.error(apiError(e)));
  }, [sezione]);
  useEffect(() => { carica(); }, [carica]);
  const q = cerca.trim().toLowerCase();
  const righe = dati.rows.filter((d) => !q || `${d.titolo} ${d.descrizione} ${d.nome}`.toLowerCase().includes(q));
  const puo = dati.puo_caricare || user?.role === "MASTER";
  return (
    <div className="space-y-6" data-testid={`pagina-${sezione}`}>
      <div className="flex flex-wrap items-end justify-between gap-4">
        <SectionTitle sub={cfg.sotto}>{cfg.titolo}</SectionTitle>
        <TornaHome />
      </div>
      {puo && <Caricamento sezione={sezione} onDone={carica} />}
      <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca per titolo, descrizione o nome file…"
        className="w-full md:w-96 rounded-md border border-slate-300 px-3 py-2 text-sm bg-white" data-testid="documenti-cerca" />
      <div className="space-y-3">
        {righe.length === 0 && <Card className="p-8 text-center text-sm text-slate-400" data-testid="documenti-vuoto">Nessun file presente.</Card>}
        {righe.map((d) => <Documento key={d.id} d={d} puo={puo} onChange={carica} />)}
      </div>
    </div>
  );
}
